import type { ReactNode } from "react";
import type { BillingConfigTiers } from "../../types";
import { METRIC_ICONS } from "../../util";
import { CriteriaDisplay } from "./criteria-display";
import { PriceDisplay } from "./price-display";
import { TierHeader } from "./tier-header";
import { sortPrices } from "./utils";

export function TierGrid({
  tiers,
}: {
  tiers: BillingConfigTiers;
}): ReactNode {
  return (
    <>
      {tiers.map((tier, idx) => {
        const prices = sortPrices(tier.prices);
        const showSidebar =
          tier.title || tier.description || tier.criteria.length > 0;
        return (
          <div
            key={idx}
            className={`bg-r8-gray-1 border border-r8-gray-12 ${
              showSidebar
                ? "@5xl:grid @5xl:grid-cols-2"
                : "flex flex-col divide-y justify-center flex-1 [&:has(>div:only-child)]:items-center"
            }`}
          >
            {showSidebar ? (
              <>
                <div className="bg-white dark:bg-black @5xl:border-r divide-y">
                  <TierHeader
                    className="p-3"
                    title={tier.title}
                    description={tier.description}
                  />
                  {tier.criteria.length > 0 && (
                    <div className="space-y-2 p-3">
                      <p className="text-r8-sm text-r8-gray-10">Criteria</p>
                      <CriteriaDisplay criteria={tier.criteria} />
                    </div>
                  )}
                </div>
                <div className="grid items-stretch relative -mb-0.5 border-t flex-1 @5xl:border-t-0">
                  {prices.length > 0 ? (
                    prices.map((price, i) => (
                      <div className="p-3" key={i}>
                        <PriceDisplay price={price} />
                      </div>
                    ))
                  ) : (
                    <div className="p-3">
                      <p className="text-r8-sm text-r8-gray-10">
                        No charge on the Replicate platform, but costs may apply
                        via third-party services
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              prices.map((price, i) => {
                const Icon = METRIC_ICONS[price.metric];

                return (
                  <div className="p-4 flex gap-4 items-center" key={i}>
                    <Icon size={50} weight="light" />
                    <div>
                      <div className="flex items-baseline gap-2">
                        <div className="font-heading font-semibold text-r8-xl break-anywhere text-r8-gray-12">
                          {price.price}
                        </div>
                        <div>{price.title}</div>
                      </div>
                      {price.description && (
                        <p className="text-r8-sm text-r8-gray-11 group-hover:text-r8-gray-12 transition-colors">
                          {price.description}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );
      })}
    </>
  );
}
